import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, authFetch } from "../../config/api";

const money = (value) =>
  Number(value || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-PH", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatRelativeTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / 60000);
  const hours = Math.round(diff / 3600000);
  const days = Math.round(diff / 86400000);

  if (Math.abs(minutes) < 60) return `${Math.max(1, Math.abs(minutes))}m ago`;
  if (Math.abs(hours) < 24) return `${Math.max(1, Math.abs(hours))}h ago`;
  return `${Math.max(1, Math.abs(days))}d ago`;
};

const normalizeStatus = (value) => String(value || "").toLowerCase();

const getReturnDueAmount = (row) => {
  const signedDue = Number(row?.liquidation_report?.total_reimbursement_return || 0);
  return signedDue < 0 ? Math.abs(signedDue) : 0;
};

const getReturnPaidAmount = (row) => Number(row?.liquidation?.return_amount || 0);

const getReturnOutstandingAmount = (row) => Math.max(getReturnDueAmount(row) - getReturnPaidAmount(row), 0);

const getDateRange = (filter, customStartDate, customEndDate) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case "today":
      return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case "week": {
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
      return { start: startOfWeek, end: endOfWeek };
    }
    case "month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start: startOfMonth, end: endOfMonth };
    }
    case "custom":
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setDate(end.getDate() + 1);
        return { start, end };
      }
      return null;
    default:
      return null;
  }
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return JSON.parse(raw);
  }

  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const DashboardCard = ({ title, value, subtitle, tone = "blue", icon, onClick }) => {
  const toneClasses = {
    blue: "border-blue-100 bg-blue-50 text-blue-700",
    green: "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    red: "border-red-100 bg-red-50 text-red-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</p>
          {subtitle ? <p className="mt-1 text-[11px] leading-4 text-slate-500">{subtitle}</p> : null}
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${toneClasses[tone] || toneClasses.blue}`}>
          <i className={`fa-solid text-sm ${icon}`} />
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue/25 hover:shadow-md"
      >
        {body}
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      {body}
    </div>
  );
};

const SectionShell = ({ title, subtitle, action, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm">
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-blue">{title}</h2>
        {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const AccountingDashboard = () => {
  const navigate = useNavigate();

  const [dateFilter, setDateFilter] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customDateDraftStart, setCustomDateDraftStart] = useState("");
  const [customDateDraftEnd, setCustomDateDraftEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [costAnalyses, setCostAnalyses] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [productionRows, setProductionRows] = useState([]);
  const [productionSummary, setProductionSummary] = useState(null);
  const previousDateFilterRef = useRef("all");

  const loadDashboard = async ({
    dateFilterValue = dateFilter,
    startDateValue = customStartDate,
    endDateValue = customEndDate,
  } = {}) => {
    setLoading(true);
    setError("");
    try {
      const range = getDateRange(dateFilterValue, startDateValue, endDateValue);

      const loadAllRequestRows = async () => {
        const requestUrl = new URL(`${API_BASE_URL}/api/accounting/requests`);
        requestUrl.searchParams.set("page", "1");
        requestUrl.searchParams.set("pageSize", "200");
        requestUrl.searchParams.set("dateFilter", dateFilterValue || "all");
        if (dateFilterValue === "custom") {
          requestUrl.searchParams.set("customStart", startDateValue || "");
          requestUrl.searchParams.set("customEnd", endDateValue || "");
        }

        const firstResponse = await authFetch(requestUrl);
        const firstJson = await readJsonResponse(firstResponse);
        if (!firstJson.success) return firstJson;

        const firstRows = firstJson.data || [];
        const totalCount = Number(firstJson.pagination?.totalCount || firstRows.length);
        const pageSize = 200;
        const totalPages = Math.ceil(totalCount / pageSize);

        if (totalPages <= 1) return { ...firstJson, data: firstRows };

        const remainingResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) => {
            const pageUrl = new URL(requestUrl);
            pageUrl.searchParams.set("page", String(index + 2));
            return authFetch(pageUrl).then((response) => readJsonResponse(response));
          })
        );

        return {
          ...firstJson,
          data: [
            ...firstRows,
            ...remainingResponses.flatMap((page) => page.success ? page.data || [] : []),
          ],
        };
      };

      const productionUrl = new URL(`${API_BASE_URL}/api/accounting/production-report`);
      if (range) {
        productionUrl.searchParams.set("startDate", range.start.toISOString().slice(0, 10));
        productionUrl.searchParams.set("endDate", range.end.toISOString().slice(0, 10));
      }

      const productionSummaryUrl = new URL(`${API_BASE_URL}/api/accounting/production-report/summary`);
      if (range) {
        productionSummaryUrl.searchParams.set("startDate", range.start.toISOString().slice(0, 10));
        productionSummaryUrl.searchParams.set("endDate", range.end.toISOString().slice(0, 10));
      }

      const [requestsJson, costRes, liquidationRes, vouchersRes, productionSummaryRes] = await Promise.all([
        loadAllRequestRows(),
        authFetch(`${API_BASE_URL}/api/accounting/cost-analysis`),
        authFetch(`${API_BASE_URL}/api/accounting/liquidations`),
        authFetch(`${API_BASE_URL}/api/accounting/check-vouchers`),
        authFetch(productionSummaryUrl),
      ]);

      const [costJson, liquidationJson, vouchersJson, productionSummaryJson] = await Promise.all([
        readJsonResponse(costRes),
        readJsonResponse(liquidationRes),
        readJsonResponse(vouchersRes),
        readJsonResponse(productionSummaryRes),
      ]);

      if (!requestsJson.success) throw new Error(requestsJson.message || "Failed to load requests");
      if (!costJson.success) throw new Error(costJson.message || "Failed to load cost analysis");
      if (!liquidationJson.success) throw new Error(liquidationJson.message || "Failed to load liquidations");
      if (!vouchersJson.success) throw new Error(vouchersJson.message || "Failed to load vouchers");
      if (!productionSummaryJson.success) {
        throw new Error(productionSummaryJson.message || "Failed to load production summary");
      }

      setRequests(requestsJson.data || []);
      setCostAnalyses(costJson.data || []);
      setLiquidations(liquidationJson.data || []);
      setVouchers(vouchersJson.data || []);
      setProductionSummary(productionSummaryJson.data || null);
      setProductionRows([]);

      // Detailed production rows are only needed for shipment status and the
      // recent activity list. Load them after the dashboard's critical data is
      // rendered so a large report cannot block the initial screen.
      authFetch(productionUrl)
        .then((response) => readJsonResponse(response))
        .then((productionJson) => {
          if (productionJson.success) setProductionRows(productionJson.data || []);
        })
        .catch((productionError) => {
          console.error("Load detailed dashboard production rows", productionError);
        });
    } catch (err) {
      console.error("load accounting dashboard data", err);
      setError(err.message || "Failed to load accounting dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilter = () => loadDashboard();
  const openCustomDateModal = () => {
    setCustomDateDraftStart(customStartDate);
    setCustomDateDraftEnd(customEndDate);
    setCustomDateModalOpen(true);
  };
  const closeCustomDateModal = () => {
    setCustomDateModalOpen(false);
  };
  const applyCustomDateRange = () => {
    setCustomStartDate(customDateDraftStart);
    setCustomEndDate(customDateDraftEnd);
    setDateFilter("custom");
    setCustomDateModalOpen(false);
  };
  const resetFilter = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomDateModalOpen(false);
    setCustomDateDraftStart("");
    setCustomDateDraftEnd("");
    previousDateFilterRef.current = "all";
    loadDashboard({ dateFilterValue: "all", startDateValue: "", endDateValue: "" });
  };

  const getActiveDateRangeLabel = () => {
    if (dateFilter === "all") return "All Dates";
    if (dateFilter === "custom") {
      if (customStartDate && customEndDate) {
        return `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`;
      }
      return "Custom Range";
    }

    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return "All Dates";
    const inclusiveEnd = new Date(range.end.getTime() - 24 * 60 * 60 * 1000);
    return `${formatDate(range.start)} - ${formatDate(inclusiveEnd)}`;
  };

  const filteredRequests = useMemo(() => {
    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return requests;
    return requests.filter((request) => {
      const createdAt = request.created_at ? new Date(request.created_at) : null;
      return createdAt ? createdAt >= range.start && createdAt < range.end : false;
    });
  }, [customEndDate, customStartDate, dateFilter, requests]);

  const filteredCostAnalyses = useMemo(() => {
    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return costAnalyses;
    return costAnalyses.filter((row) => {
      const createdAt = row.updated_at || row.created_at || row.cost_analysis?.updated_at || row.cost_analysis?.created_at;
      if (!createdAt) return false;
      const date = new Date(createdAt);
      return date >= range.start && date < range.end;
    });
  }, [costAnalyses, customEndDate, customStartDate, dateFilter]);

  const filteredLiquidations = useMemo(() => {
    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return liquidations;
    return liquidations.filter((row) => {
      const createdAt =
        row.liquidation_report?.report_date_created ||
        row.liquidation_report?.created_at ||
        row.liquidation?.updated_at ||
        row.liquidation?.created_at ||
        row.updated_at ||
        row.created_at;
      if (!createdAt) return false;
      const date = new Date(createdAt);
      return date >= range.start && date < range.end;
    });
  }, [customEndDate, customStartDate, dateFilter, liquidations]);

  const filteredVouchers = useMemo(() => {
    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return vouchers;
    return vouchers.filter((voucher) => {
      const voucherDate = voucher.voucher_date || voucher.created_at;
      if (!voucherDate) return false;
      const date = new Date(voucherDate);
      return date >= range.start && date < range.end;
    });
  }, [customEndDate, customStartDate, dateFilter, vouchers]);

  const filteredProductionRows = useMemo(() => {
    const range = getDateRange(dateFilter, customStartDate, customEndDate);
    if (!range) return productionRows;
    return productionRows.filter((row) => {
      const reportDate = row.date_delivered || row.liquidation_date;
      if (!reportDate) return true;
      const date = new Date(reportDate);
      return date >= range.start && date < range.end;
    });
  }, [customEndDate, customStartDate, dateFilter, productionRows]);

  const requestStats = useMemo(() => {
    const total = filteredRequests.length;
    const approved = filteredRequests.filter((row) => normalizeStatus(row.status) === "approved").length;
    const pending = filteredRequests.filter((row) =>
      ["pending", "partially_approved", "submitted"].includes(normalizeStatus(row.status))
    ).length;
    const forLiquidation = filteredRequests.filter(
      (row) => row.for_liquidation && normalizeStatus(row.status) === "approved"
    ).length;
    const pendingApproval = filteredRequests.filter((row) =>
      ["pending", "partially_approved", "submitted"].includes(normalizeStatus(row.status))
    ).length;

    return { total, approved, pending, forLiquidation, pendingApproval };
  }, [filteredRequests]);

  const costAnalysisStats = useMemo(() => {
    const total = filteredCostAnalyses.length;
    const submitted = filteredCostAnalyses.filter((row) => normalizeStatus(row.cost_analysis_stage) === "submitted").length;
    const partiallyApproved = filteredCostAnalyses.filter((row) => ["team_leader_approved", "partially_approved", "verified"].includes(normalizeStatus(row.cost_analysis_stage))).length;
    const approved = filteredCostAnalyses.filter((row) => normalizeStatus(row.cost_analysis_stage) === "approved").length;
    const draft = filteredCostAnalyses.filter((row) => normalizeStatus(row.cost_analysis_stage) === "pending_submission").length;

    return { total, submitted, partiallyApproved, approved, draft };
  }, [filteredCostAnalyses]);

  const liquidationStats = useMemo(() => {
    const total = filteredLiquidations.length;
    const submitted = filteredLiquidations.filter((row) => normalizeStatus(row.liquidation_stage) === "submitted").length;
    const verified = filteredLiquidations.filter((row) => normalizeStatus(row.liquidation_stage) === "verified").length;
    const approved = filteredLiquidations.filter((row) => normalizeStatus(row.liquidation_stage) === "approved").length;
    const pending = filteredLiquidations.filter((row) =>
      ["pending_submission", "submitted"].includes(normalizeStatus(row.liquidation_stage))
    ).length;
    const reimbursement = filteredLiquidations.reduce((sum, row) => {
      const reimbReturnValue = Number(row.liquidation_report?.total_reimbursement_return || 0);
      return sum + (reimbReturnValue > 0 ? reimbReturnValue : 0);
    }, 0);
    const returnOutstanding = filteredLiquidations.reduce((sum, row) => sum + getReturnOutstandingAmount(row), 0);
    const returnSettled = filteredLiquidations.reduce(
      (sum, row) => sum + (getReturnDueAmount(row) > 0 && getReturnOutstandingAmount(row) === 0 && getReturnPaidAmount(row) > 0 ? 1 : 0),
      0
    );
    const returnPartial = filteredLiquidations.reduce(
      (sum, row) => sum + (getReturnDueAmount(row) > 0 && getReturnOutstandingAmount(row) > 0 && getReturnPaidAmount(row) > 0 ? 1 : 0),
      0
    );

    return { total, submitted, verified, approved, pending, reimbursement, returnOutstanding, returnSettled, returnPartial };
  }, [filteredLiquidations]);

  const voucherStats = useMemo(() => {
    const total = filteredVouchers.length;
    const checked = filteredVouchers.filter((row) => row.checked_by_user_id || row.checked_by_name).length;
    const pending = total - checked;
    return { total, checked, pending };
  }, [filteredVouchers]);

  const productionStats = useMemo(() => {
    if (!filteredProductionRows.length && productionSummary) {
      const totalBilling = Number(productionSummary.totalBillingCharges || 0);
      const totalCosts = Number(productionSummary.totalActualCosts || 0);
      const totalCollected = Number(productionSummary.totalCollected || 0);
      const totalGrossProfit = Number(
        productionSummary.totalGrossProfit ?? totalBilling - totalCosts
      );

      return {
        totalBilling,
        totalCosts,
        totalGrossProfit,
        totalCollected,
        outstanding: totalBilling - totalCollected,
        paidShipments: 0,
        unpaidShipments: 0,
        avgCostPercentage: totalBilling > 0 ? (totalCosts / totalBilling) * 100 : 0,
        avgProfitPercentage: totalBilling > 0 ? (totalGrossProfit / totalBilling) * 100 : 0,
      };
    }

    const totalBilling = filteredProductionRows.reduce((sum, row) => sum + Number(row.sell_rate || 0), 0);
    const totalCosts = filteredProductionRows.reduce((sum, row) => sum + Number(row.cost_rate || 0), 0);
    const totalGrossProfit = totalBilling - totalCosts;
    const totalCollected = filteredProductionRows.reduce((sum, row) => sum + Number(row.amount_collected || 0), 0);
    const outstanding = totalBilling - totalCollected;
    const paidShipments = filteredProductionRows.filter((row) => Boolean(row.is_paid)).length;
    const unpaidShipments = filteredProductionRows.filter((row) => !Boolean(row.is_paid)).length;
    const avgCostPercentage = totalBilling > 0 ? (totalCosts / totalBilling) * 100 : 0;
    const avgProfitPercentage = totalBilling > 0 ? (totalGrossProfit / totalBilling) * 100 : 0;

    return {
      totalBilling,
      totalCosts,
      totalGrossProfit,
      totalCollected,
      outstanding,
      paidShipments,
      unpaidShipments,
      avgCostPercentage,
      avgProfitPercentage,
    };
  }, [filteredProductionRows, productionSummary]);

  const summaryLine = `${requestStats.total} requests, ${costAnalysisStats.total} cost analyses, ${liquidationStats.total} liquidations, and ${voucherStats.total} vouchers in the selected range.`;

  // These sections are intentionally omitted from the dashboard view to keep
  // the initial page lightweight.
  const quickActions = [];
  const latestRequests = [];
  const latestCostAnalyses = [];
  const latestLiquidations = [];
  const latestVouchers = [];
  const latestPaidShipments = [];
  const attentionItems = [];
  const renderActivityRow = () => null;

  const topCards = [
    { title: "Total Requests", value: requestStats.total, tone: "blue", icon: "fa-file-lines" },
    { title: "Approved Requests", value: requestStats.approved, tone: "green", icon: "fa-circle-check" },
    { title: "Pending Requests", value: requestStats.pending, tone: "amber", icon: "fa-hourglass-half" },
    { title: "For Liquidation", value: requestStats.forLiquidation, tone: "red", icon: "fa-receipt" },
    { title: "Cost Analyses", value: costAnalysisStats.total, tone: "blue", icon: "fa-calculator" },
    { title: "Check Vouchers", value: voucherStats.total, tone: "green", icon: "fa-money-check-dollar" },
    { title: "Paid Shipments", value: productionStats.paidShipments, tone: "green", icon: "fa-sack-dollar" },
    { title: "Unpaid Shipments", value: productionStats.unpaidShipments, tone: "amber", icon: "fa-triangle-exclamation" },
  ];

  const progressCards = [
    {
      title: "Request Submitted",
      count: requestStats.pendingApproval,
      tone: "amber",
      description: "Requests waiting to be approved",
      icon: "fa-paper-plane",
    },
    {
      title: "Request Approved",
      count: requestStats.approved,
      tone: "green",
      description: "Requests ready for downstream workflows",
      icon: "fa-circle-check",
    },
    {
      title: "Cost Analysis Saved",
      count: costAnalysisStats.submitted + costAnalysisStats.partiallyApproved + costAnalysisStats.approved,
      tone: "blue",
      description: `${costAnalysisStats.draft} still in draft`,
      icon: "fa-calculator",
    },
    {
      title: "Liquidation Saved",
      count: liquidationStats.submitted + liquidationStats.verified + liquidationStats.approved,
      tone: "blue",
      description: `${liquidationStats.pending} still pending`,
      icon: "fa-receipt",
    },
    {
      title: "Check Voucher Created",
      count: voucherStats.total,
      tone: "green",
      description: `${voucherStats.pending} waiting for checking`,
      icon: "fa-money-check",
    },
    {
      title: "Check Voucher Checked",
      count: voucherStats.checked,
      tone: "green",
      description: "Vouchers already checked",
      icon: "fa-user-check",
    },
    {
      title: "OR Uploaded",
      count: productionStats.paidShipments,
      tone: "green",
      description: "Delivered shipments marked paid",
      icon: "fa-file-invoice",
    },
  ];

  const financialCards = [
    { title: "Total Billing", value: `₱${money(productionStats.totalBilling)}`, tone: "blue", icon: "fa-coins" },
    { title: "Total Costs", value: `₱${money(productionStats.totalCosts)}`, tone: "red", icon: "fa-wallet" },
    {
      title: "Total Gross Profit",
      value: `₱${money(productionStats.totalGrossProfit)}`,
      tone: "green",
      icon: "fa-chart-line",
    },
    { title: "Total Collected", value: `₱${money(productionStats.totalCollected)}`, tone: "blue", icon: "fa-hand-holding-dollar" },
    { title: "Outstanding", value: `₱${money(productionStats.outstanding)}`, tone: "amber", icon: "fa-circle-exclamation" },
    {
      title: "Liquidation Reimbursement",
      value: `₱${money(liquidationStats.reimbursement)}`,
      tone: "green",
      icon: "fa-file-invoice-dollar",
    },
    {
      title: "Liquidation Return",
      value: `₱${money(liquidationStats.returnOutstanding)}`,
      tone: "amber",
      icon: "fa-rotate-left",
      subtitle:
        liquidationStats.returnPartial > 0
          ? `Partial settlement: ₱${money(liquidationStats.returnOutstanding)} remaining`
          : liquidationStats.returnOutstanding > 0
            ? `Pending settlement: ₱${money(liquidationStats.returnOutstanding)}`
            : "Settled via return receipt",
    },
  ];

  return (
    <div className="space-y-5 font-poppins">
      <header className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Accounting</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-blue sm:text-3xl">
              Accounting Dashboard
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{summaryLine}</p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 xl:max-w-[48rem] xl:self-start">
            {[
              { key: "today", label: "Today" },
              { key: "week", label: "This Week" },
              { key: "month", label: "This Month" },
              { key: "custom", label: "Custom Range" },
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => {
                  if (chip.key === "custom") {
                    previousDateFilterRef.current = dateFilter;
                    setDateFilter(previousDateFilterRef.current || "all");
                    openCustomDateModal();
                    return;
                  }
                  setDateFilter(chip.key);
                }}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                  dateFilter === chip.key
                    ? "bg-blue text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {chip.label}
              </button>
            ))}
            <button
              type="button"
              onClick={applyFilter}
              className="rounded-full bg-slate-900 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Search
            </button>
            <button
              type="button"
              onClick={resetFilter}
              className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-600">
          <span className="font-semibold text-slate-700">Active Date Range:</span>{" "}
          <span className="text-blue">{getActiveDateRangeLabel()}</span>
        </div>
      </header>

      {customDateModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-blue">Custom Date Range</h2>
                <p className="text-sm text-slate-500">Choose the start and end date for the dashboard.</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customDateDraftStart}
                    onChange={(e) => setCustomDateDraftStart(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customDateDraftEnd}
                    onChange={(e) => setCustomDateDraftEnd(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={closeCustomDateModal}
                  className="inline-flex items-center justify-center rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCustomDateRange}
                  disabled={!customDateDraftStart || !customDateDraftEnd}
                  className="inline-flex items-center justify-center rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
          Loading accounting dashboard...
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section>
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-blue">Top KPIs</h2>
            <p className="text-xs text-slate-500">Core accounting activity in the selected range.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {topCards.map((card) => (
            <DashboardCard key={card.title} {...card} />
          ))}
        </div>
      </section>

      {false && <SectionShell
        title="Workflow Progress"
        subtitle="Counts across the accounting process."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {progressCards.map((card) => (
            <div key={card.title} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                  card.tone === "green"
                    ? "bg-emerald-50 text-emerald-700"
                    : card.tone === "amber"
                      ? "bg-amber-50 text-amber-700"
                      : card.tone === "red"
                        ? "bg-red-50 text-red-700"
                        : "bg-blue-50 text-blue-700"
                }`}>
                  <i className={`fa-solid text-sm ${card.icon}`} />
                </div>
                <span className="text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{card.count}</span>
              </div>
              <div className="mt-3 text-sm font-semibold text-slate-900">{card.title}</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">{card.description}</div>
            </div>
          ))}
        </div>
      </SectionShell>}

      <SectionShell title="Financial Snapshot" subtitle="Production report totals and collection status.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {financialCards.map((card) => (
            <DashboardCard key={card.title} {...card} />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Average Cost %</div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-blue">{productionStats.avgCostPercentage.toFixed(2)}%</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Average Profit %</div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-emerald-700">
              {productionStats.avgProfitPercentage.toFixed(2)}%
            </div>
          </div>
        </div>
      </SectionShell>

      {false && <SectionShell title="Quick Actions" subtitle="Direct links to the main accounting screens.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {quickActions.map((action) => (
            <button
              key={action.title}
              type="button"
              onClick={() => navigate(action.to)}
              className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue/30 hover:shadow-md"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue/10 text-blue">
                    <i className={`fa-solid ${action.icon}`} />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{action.title}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{action.value}</div>
                  </div>
                </div>
                <i className="fa-solid fa-arrow-right text-slate-300 transition group-hover:text-blue" />
              </div>
            </button>
          ))}
        </div>
      </SectionShell>}

      {false && <SectionShell
        title="Recent Activity"
        subtitle="Latest items across the main accounting workflows."
        action={
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            <span className="h-2 w-2 rounded-full bg-blue" />
            Live feed
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Requests</div>
            <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{latestRequests.length}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Cost Analysis</div>
            <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{latestCostAnalyses.length}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Liquidations</div>
            <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{latestLiquidations.length}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">OR Uploads</div>
            <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{latestPaidShipments.length}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Latest Requests</div>
                <div className="text-xs text-slate-500">Recently submitted accounting requests</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/accounting/requests")}
                className="text-xs font-semibold text-blue hover:underline"
              >
                View all
              </button>
            </div>
            <div className="space-y-2">
              {latestRequests.length ? (
                latestRequests.map((row) =>
                  renderActivityRow(
                    row,
                    `${row.rfp_no || "—"} • ${row.job_order || "—"}`,
                    `${row.payee || "—"} • ₱${money(row.total_amount || 0)} • ${formatRelativeTime(row.created_at)}`,
                    normalizeStatus(row.status) || "request",
                    () => navigate("/accounting/requests")
                  )
                )
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No requests found for this range.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Latest Cost Analysis</div>
                <div className="text-xs text-slate-500">Most recent analysis documents</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/accounting/cost-analysis")}
                className="text-xs font-semibold text-blue hover:underline"
              >
                View all
              </button>
            </div>
            <div className="space-y-2">
              {latestCostAnalyses.length ? (
                latestCostAnalyses.map((row) =>
                  renderActivityRow(
                    row,
                    `${row.rfp_no || "—"} • ${row.job_order || "—"}`,
                    `${row.payee || "—"} • ${row.cost_analysis_stage || "draft"} • ${formatRelativeTime(
                      row.updated_at || row.created_at
                    )}`,
                    row.cost_analysis_stage || "draft",
                    () => navigate("/accounting/cost-analysis")
                  )
                )
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No cost analysis entries found for this range.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Latest Liquidations</div>
                <div className="text-xs text-slate-500">Verified and submitted liquidation reports</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/accounting/liquidation")}
                className="text-xs font-semibold text-blue hover:underline"
              >
                View all
              </button>
            </div>
            <div className="space-y-2">
              {latestLiquidations.length ? (
                latestLiquidations.map((row) =>
                  renderActivityRow(
                    row,
                    `${row.rfp_no || "—"} • ${row.job_order || "—"}`,
                    `${row.payee || "—"} • ${row.liquidation_stage || "draft"} • ${formatRelativeTime(
                      row.liquidation_report?.report_date_created ||
                        row.liquidation_report?.created_at ||
                        row.updated_at ||
                        row.created_at
                    )}`,
                    row.liquidation_stage || "draft",
                    () => navigate("/accounting/liquidation")
                  )
                )
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No liquidations found for this range.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Latest Check Vouchers</div>
                <div className="text-xs text-slate-500">Checking status and amounts</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/accounting/check-vouchers")}
                className="text-xs font-semibold text-blue hover:underline"
              >
                View all
              </button>
            </div>
            <div className="space-y-2">
              {latestVouchers.length ? (
                latestVouchers.map((row) =>
                  renderActivityRow(
                    row,
                    `${row.voucher_no || "—"} • ${row.payee || "—"}`,
                    `₱${money(row.amount || 0)} • ${row.checked_by_name || "Pending"} • ${formatRelativeTime(
                      row.created_at || row.voucher_date
                    )}`,
                    row.checked_by_name ? "checked" : "pending",
                    () => navigate("/accounting/check-vouchers")
                  )
                )
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No vouchers found for this range.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 xl:col-span-2">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Latest OR Uploads</div>
                <div className="text-xs text-slate-500">Recently marked paid shipments</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/accounting/production-report")}
                className="text-xs font-semibold text-blue hover:underline"
              >
                View all
              </button>
            </div>
            {latestPaidShipments.length ? (
              <div className="grid gap-2 md:grid-cols-2">
                {latestPaidShipments.map((row) =>
                  renderActivityRow(
                    row,
                    `${row.job_order_no || "—"} • ${row.client_name || "—"}`,
                    `${row.date_delivered ? formatDate(row.date_delivered) : "—"} • Paid • ${formatRelativeTime(
                      row.date_delivered
                    )}`,
                    "paid",
                    () => navigate("/accounting/production-report")
                  )
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                No paid shipments found for this range.
              </div>
            )}
          </div>
        </div>
      </SectionShell>}

      {false && <SectionShell title="Attention Panel" subtitle="Items that need action now.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {attentionItems.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={() => navigate(item.to)}
              className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{item.title}</p>
                  <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{item.count}</p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${
                    item.tone === "red"
                      ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                      : item.tone === "amber"
                        ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                        : "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                  }`}
                >
                  Alert
                </span>
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">{item.subtitle}</p>
            </button>
          ))}
        </div>
      </SectionShell>}
    </div>
  );
};

export default AccountingDashboard;
